import {MathUtils} from 'three';
import {FLIGHT_LAUNCH_TIME,FLIGHT_LAUNCH_WINDUP,FLIGHT_LAUNCH_TOTAL} from '../../../shared/src/flightPhysics';
export {FLIGHT_LAUNCH_TIME,FLIGHT_LAUNCH_THRUST,FLIGHT_LAUNCH_WINDUP,FLIGHT_LAUNCH_TOTAL,flightLaunchRetention} from '../../../shared/src/flightPhysics';

/** Sample the whole authored transition inside the free deployment window.
 * Remaining simulation time also lets late remote snapshots join mid-clip. */
export function flightLaunchClipProgress(remaining:number):number {
  if(!Number.isFinite(remaining)||remaining<=0)return 1;
  // The tuck provides anticipation while the player aims. Forward extension
  // and the full source tail play only once propulsion actually starts.
  return remaining>FLIGHT_LAUNCH_TIME
    ? .35*MathUtils.clamp((FLIGHT_LAUNCH_TOTAL-remaining)/FLIGHT_LAUNCH_WINDUP,0,1)
    : .35+.65*MathUtils.clamp(1-remaining/FLIGHT_LAUNCH_TIME,0,1);
}

/** An interrupted launch fades from its last rendered source pose. Seeking its
 * endpoint immediately would skip the body through the rest of the gesture
 * while the outgoing action still has substantial mixer weight. */
export class FlightLaunchPoseClock {
  progress=1;
  private remaining=0;
  private interrupted=false;
  private releaseAge=.18;
  get blendRate():number{return MathUtils.smoothstep(this.releaseAge,0,.18);}
  reset():void {this.progress=1;this.remaining=0;this.interrupted=false;this.releaseAge=.18;}
  update(remaining:number,dt:number,divePreparation=0):number {
    const elapsed=Math.max(0,Number.isFinite(dt)?dt:0);
    this.releaseAge=Math.min(.18,this.releaseAge+elapsed);
    const next=Number.isFinite(remaining)?MathUtils.clamp(remaining,0,FLIGHT_LAUNCH_TOTAL):0;
    if(next>0){this.progress=flightLaunchClipProgress(next);this.interrupted=false;this.releaseAge=.18;}
    else if(this.remaining>0){
      this.interrupted=this.remaining>elapsed+1e-6;
      if(!this.interrupted)this.progress=1;
      // Ctrl can interrupt at the deepest tuck. Release from zero blend speed
      // instead of straightening the bent limbs on the first preparation tick.
      if(this.interrupted&&divePreparation>0)this.releaseAge=0;
    }else if(!this.interrupted)this.progress=1;
    this.remaining=next;
    return this.progress;
  }
}

/** Reuse the startup gesture for Ctrl without reusing its propulsion timer.
 * Preparation samples the anticipation; gravity begins while the source's
 * follow-through finishes. Cancelling holds the outgoing source pose. */
export class FlightDiveStartClock {
  remaining=0;
  orientation=0;
  readonly pose=new FlightLaunchPoseClock();
  private preparation=0;
  private wasDiving=false;
  private seen=false;
  private autoAge=1;
  private automatic=false;
  get automaticEntry():boolean{return this.automatic;}
  get entryAge():number{return this.autoAge;}
  get weight():number {
    if(this.remaining<=0)return 0;
    // A Ctrl gather starts in the deep tuck; keep some of the arriving plunge
    // underneath so its fast knee changes do not become a second impulse.
    return (this.automatic?.72:.65)*MathUtils.smoothstep(FLIGHT_LAUNCH_TOTAL-this.remaining,0,.1)
      *MathUtils.smoothstep(this.remaining,0,.55)*MathUtils.smoothstep(this.autoAge,0,.08);
  }
  reset():void {this.remaining=this.orientation=0;this.preparation=0;this.wasDiving=false;this.seen=false;this.autoAge=1;this.automatic=false;this.pose.reset();}
  update(preparation:number,diving:boolean,dt:number):void {
    const next=Number.isFinite(preparation)?MathUtils.clamp(preparation,0,1):0;
    this.autoAge+=Math.max(0,dt);
    if(next>0){this.remaining=FLIGHT_LAUNCH_TIME+FLIGHT_LAUNCH_WINDUP*(1-next);this.autoAge=1;this.automatic=false;}
    else if(this.preparation>0)this.remaining=diving?FLIGHT_LAUNCH_TIME:0;
    else if(diving&&!this.wasDiving&&this.seen){this.remaining=FLIGHT_LAUNCH_TIME;this.autoAge=0;this.automatic=true;}
    else this.remaining=diving?Math.max(0,this.remaining-Math.max(0,dt)):0;
    this.orientation=next>0||diving&&(this.remaining>0||this.orientation>0)?1:this.orientation*Math.exp(-6*Math.max(0,dt));
    if(this.orientation<1e-5)this.orientation=0;
    this.pose.update(this.remaining,dt);
    if(this.remaining>0)this.pose.progress=flightDiveStartClipProgress(this.remaining);
    this.preparation=next;this.wasDiving=diving;this.seen=true;
  }
}

/** The downward variant spends more time on the source's fast leg extension.
 * Its quieter late motion accelerates only after the gesture is fading out. */
export function flightDiveStartClipProgress(remaining:number):number {
  if(!Number.isFinite(remaining))return 1;
  if(remaining>=FLIGHT_LAUNCH_TIME||remaining<=0)return flightLaunchClipProgress(remaining);
  const elapsed=FLIGHT_LAUNCH_TIME-remaining,slow=.24,rate=.2/slow;
  if(elapsed<=slow)return .35+elapsed*rate;
  const tail=elapsed-slow,span=FLIGHT_LAUNCH_TIME-slow;
  return .55+tail*rate+tail*tail*(.45-span*rate)/(span*span);
}

/** Late startup and its outgoing fade use one continuous attitude envelope.
 * The simulation timer ending must not enable an unrelated body tilt at once. */
export class FlightLaunchAlignment {
  amount=0;
  reset():void {this.amount=0;}
  update(remaining:number,dt:number):number {
    if(remaining>0)this.amount=MathUtils.smoothstep(FLIGHT_LAUNCH_TOTAL-remaining,0,FLIGHT_LAUNCH_WINDUP);
    else this.amount*=Math.exp(-6*Math.max(0,dt));
    if(this.amount<1e-6)this.amount=0;
    return this.amount;
  }
}

export function flightLaunchClipWeight(remaining:number):number {
  if(!Number.isFinite(remaining)||remaining<=0)return 0;
  const elapsed=FLIGHT_LAUNCH_TOTAL-remaining;
  // Start handing off before the source's quick final elbow extension. An
  // 80 ms target fade followed by mixer easing left that beat almost unblended.
  return MathUtils.smoothstep(elapsed,0,.06)*MathUtils.smoothstep(remaining,0,.30);
}

/** Visual preparation only: no thrust light or exhaust until the burst. */
export function flightLaunchWindup(remaining:number):number {
  return remaining>=FLIGHT_LAUNCH_TIME&&Number.isFinite(remaining)
    ? MathUtils.smoothstep(FLIGHT_LAUNCH_TOTAL-remaining,0,FLIGHT_LAUNCH_WINDUP):0;
}

/** One deployment beat; held paid boost never replays this envelope. */
export function flightLaunchPulse(remaining:number):number {
  if(!Number.isFinite(remaining)||remaining<=0)return 0;
  const phase=Math.max(0,1-remaining/FLIGHT_LAUNCH_TIME);
  return MathUtils.smoothstep(phase,0,.125)*(1-MathUtils.smoothstep(phase,.6,1));
}
